import { useState, useEffect } from 'react';
import { Box, Paper, Stepper, Step, StepLabel, Button, Typography, Snackbar, Alert, IconButton, Divider } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ClientDetails from './components/ClientDetails';
import ProductDetails from './components/ProductDetails';
import InquiryReview from './components/InquiryReview';
import axios from 'axios';

const steps = ['Client Details', 'Product Details', 'Review & Submit'];



// Utility function for deep merging objects
const deepMerge = (target, source) => {
    const output = { ...target };
    for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            output[key] = deepMerge(target[key] || {}, source[key]);
        } else {
            output[key] = source[key];
        }
    }
    return output;
};

const OrderTestMaster = ({ loggedInUser, companyData, mode, connectionString }) => {
    const API_URL = `${connectionString}/api`;
    const navigate = useNavigate();
    const location = useLocation();
    const { SOAutoID } = useParams(); // Extract SOAutoID from URL
    const searchParams = new URLSearchParams(location.search);
    const quotationId = searchParams.get("quotationId");

    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [consignerEnabled, setConsignerEnabled] = useState(true);
    const [transporterEnabled, setTransporterEnabled] = useState(true);
    const [exportEnabled, setExportEnabled] = useState(false);
    const [clientId, setClientId] = useState(null);
    const [selectedSOIDs, setSelectedSOIDs] = useState(SOAutoID ? [SOAutoID] : []);

    // Fetch quotation data for 'order' mode
    useEffect(() => {
        if (mode === "order" && quotationId) {
            console.log("Mode is 'order' and Received Quotation ID:", quotationId);
            const fetchQuotationData = async () => {
                try {
                    const res = await axios.get(`${API_URL}/quot-Order-ref/${quotationId}`);
                    const data = res.data;
                    console.log('Fetched quotation data:', data);
                    setFormData(data);
                    setClientId(data.client?.clientId || null); // Extract clientId from quotation data
                } catch (error) {
                    console.error('Error fetching quotation data:', error);
                    setSnackbar({ open: true, message: 'Failed to fetch quotation data.', severity: 'error' });
                }
            };
            fetchQuotationData();
        }
    }, [mode, quotationId]);

    // Fetch sales order data for 'edit' mode
    useEffect(() => {
        const fetchSalesOrders = async () => {
            try {
                console.log('Fetching sales order data for SOIDs:', selectedSOIDs);
                const response = await axios.post(`${API_URL}/orders-getInfo`, { selectedSOIDs });
                const data = response.data;
                console.log('Fetched sales order data:', data);

                // Handle both single object and array responses
                const salesOrderData = Array.isArray(data) ? data[0] : data;
                if (!salesOrderData) {
                    throw new Error('No sales order data returned');
                }

                // Transform data to match formData structure
                setFormData(salesOrderData);
                setClientId(salesOrderData.client?.clientId || null);
            } catch (error) {
                console.error('Error fetching sales orders:', error.response?.data || error.message);
                setSnackbar({ open: true, message: 'Failed to fetch sales order data.', severity: 'error' });
            }
        };

        if (mode === 'edit' && selectedSOIDs.length > 0) {
            fetchSalesOrders();
        }
    }, [mode, selectedSOIDs]);

    const handleNext = (stepData = {}) => {
        console.log('Step data:', stepData);
        setFormData(prev => deepMerge(prev, stepData));
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const handleSubmit = async () => {
        try {
            const payload = {
                client: formData.client,
                product: formData.product,
                SOAutoID:SOAutoID,
                qoutId: parseInt(quotationId),
                companyId: companyData?.[0]?.CompId,
                addUserId: loggedInUser?.UserId,
            };
            console.log('Submit payload:', payload);
            if (mode === 'add' || mode === 'order') {
                await axios.post(`${API_URL}/create-order/v2`, payload);
            } else if (mode === 'edit') {
                console.log("PUT URL:", `${API_URL}/orders-update/${SOAutoID}`);
                console.log("PUT Payload:", JSON.stringify(payload, null, 2));
                await axios.put(`${API_URL}/orders-update/${SOAutoID}`, payload);
            }

            setSnackbar({ open: true, message: mode === 'add' || mode === 'order' ? 'Order created successfully!' : 'Order updated successfully!', severity: 'success', });

            setActiveStep(0);
            setFormData({});
            navigate(-1);
        } catch (error) {
            console.error('Error submitting order:', error);
            setSnackbar({ open: true, message: 'Failed to submit order.', severity: 'error' });
        }
    };

    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const renderStepHeader = (icon, title, control = null) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
                {activeStep > 0 && (
                    <IconButton onClick={handleBack} size="small" sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                )}
                <Typography variant="h6" fontWeight="bold">{icon} {title}</Typography>
            </Box>
            {control}
        </Box>
    );

    const getStepComponent = (step) => {
        switch (step) {
            case 0:
                return (
                    <ClientDetails mode={mode} initialData={formData.client || {}} loggedInUser={loggedInUser} companyData={companyData} connectionString={connectionString}
                        onNext={(data) => {
                            setClientId(data.clientId || null); // Update clientId on client step completion
                            handleNext({ client: data });
                        }}
                    />
                );

            case 1:
                return (
                    <>
                        {renderStepHeader('2️⃣', 'Product Details')}
                        <ProductDetails initialData={formData.product || {}} onNext={(data) => handleNext({ product: data })} companyData={companyData} loggedInUser={loggedInUser} clientData={formData.client || {}} connectionString={connectionString} />
                    </>
                );

            case 2:
                return (
                    <InquiryReview formData={formData} onBack={handleBack} onSubmit={handleSubmit} onEditStep={(stepIndex) => setActiveStep(stepIndex)} companyData={companyData} connectionString={connectionString} />
                );

            default:
                return null;
        }
    };

    return (
        <Box maxWidth="lg" mx="auto" px={2} py={3}>
            <Typography variant="h5" fontWeight="bold" gutterBottom> 📝 Test Order Master </Typography>

            <Paper elevation={4} sx={{ borderRadius: 3, p: 3 }}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Divider sx={{ mb: 3 }} />
                {getStepComponent(activeStep)}
            </Paper>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OrderTestMaster;